
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Bot, LineChart, Briefcase, Building2, Lightbulb, Brain, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const ChrisProfile = () => {
  const user = {
    name: "Chris Clayton",
    role: "iSPAI Commercial & Product Development Lead",
    email: "chrisclaytonsmp@gmail.com",
    solutionBounties: 8,
    domains: [
      "Artificial Intelligence",
      "Data Analytics",
      "Business Development",
      "Product Development",
      "Market Development",
      "Business Process Automation",
      "Strategic Growth",
      "Commercial Development",
      "Technology Innovation"
    ],
    industries: [{
      name: "AI & Analytics",
      icon: Brain
    }, {
      name: "Technology",
      icon: Bot
    }, {
      name: "Business Growth",
      icon: LineChart
    }, {
      name: "Enterprise",
      icon: Building2
    }, {
      name: "Innovation",
      icon: Lightbulb
    }, {
      name: "Consulting",
      icon: Briefcase
    }],
    confirmedSolutions: 35,
    confirmedKnowledgeBases: 68,
    tokenBalance: 185,
    bio: "Chris Clayton supports all commercial, product, and market development areas for iSPAI. In addition, Chris is a Partner at Gryphon Citadel in Philadelphia, where he is pivotal in supporting the delivery, development, and growth of the firm and its Artificial Intelligence and Data Analytics practice. His versatility and innovative approaches have consistently driven business growth and technological advancements for clients in across the globe. Prior to Gryphon Citadel, Chris was with Beyond Limits where he developed an AI-driven decision support system for British Petroleum (BP), leading to BP's investment in the company and its expansion from 5 to over 300 employees. Prior to Beyond Limits, Chris has held key leadership roles at ColdLight and at Electronic Ink, and his technology career began at Acappella Software. Chris' experience spans business process automation, artificial intelligence, and strategic business growth."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/48642ff5-ed03-4a6c-9181-24e0842c3292.png"
                    imageAlt="Chris Clayton"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage src="/lovable-uploads/48642ff5-ed03-4a6c-9181-24e0842c3292.png" alt="Chris Clayton" />
                      <AvatarFallback>CC</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="bg-white text-black hover:bg-gray-100">
                          BIO
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <div className="space-y-2">
                          <h4 className="text-lg font-semibold">Chris Clayton</h4>
                          <p className="text-sm text-gray-600">{user.bio}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="sm" className="bg-white text-black hover:bg-gray-100">
                      NW
                    </Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map(industry => {
                        const Icon = industry.icon;
                        return (
                          <div key={industry.name} className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg">
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link to="/solution-bounties/chris" className="text-sm text-blue-600 hover:text-blue-800">
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.3} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map(domain => (
                  <Badge key={domain} variant="outline" className="border-2 border-black text-black">
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChrisProfile;
